import React, { ActivityIndicator, FlatList, TouchableOpacity, View } from 'react-native';
import { translate } from '../../../services/translate';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { useCallback, useState } from 'react';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Hoverable, ScrollView } from 'react-native-web-hover';
import MyAppText from '../../../components/MyAppText';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import FormInput from '../../../components/FormInput';
import CustomButton from '../../../components/CustomButton';
import DeleteModal from '../../../components/DeleteModal';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { MyCheckbox } from '../../../components/MyCheckbox';
import FilePreview from '../../../components/chat/FilePreview';
import ExpandableCommentInput from '../../../components/ExpandableCommentInput';
import { createFileFromUrl } from '../../third-party-approved-companies/InscriptionsCommons';
import { RootNavigation } from '../../../Routes';

export default function FrequentlyAskedQuestionsTable({ navigation }: { navigation: RootNavigation; }) {
    const windowInfo = WindowInformation();
    const { styles, theme } = useStyles(styleSheet);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedFrequentlyAskedQuestion, setSelectedFrequentlyAskedQuestion] = useState<FrequentlyAskedQuestion | null>(null);
    const [frequentlyAskedQuestions, setFrequentlyAskedQuestions] = useState<FrequentlyAskedQuestion[]>([]);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [toDeleteQuestion, setToDeleteQuestion] = useState<number | null>();

    async function getFrequentlyAskedQuestions() {
        try {
            setIsLoading(true);
            const response = await platformSettingsService.getFrequentlyAskedQuestions();

            for (const faq of response) {
                if (faq.fileUrl && faq.fileName) {
                    faq.file = await createFileFromUrl(faq.fileUrl, faq.fileName);
                }
            }

            setFrequentlyAskedQuestions(response);
            if (response.length) {
                setSelectedFrequentlyAskedQuestion(response[0]);
            } else {
                setSelectedFrequentlyAskedQuestion(null);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function saveFrequentlyAskedQuestions() {
        try {
            await platformSettingsService.updateFrequentlyAskedQuestions({
                frequentlyAskedQuestions: frequentlyAskedQuestions.filter(faq => faq.question !== '' && faq.answer !== '')
            });
            getFrequentlyAskedQuestions();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getFrequentlyAskedQuestions();
        }
    }, [isFocused, navigation]));

    function renderItem({ item }: { item: FrequentlyAskedQuestion; }) {
        return <TouchableOpacity
            onPress={() => setSelectedFrequentlyAskedQuestion(item)}
        >
            <Hoverable>
                {({ hovered }) => (
                    <View style={styles.row(item.id == selectedFrequentlyAskedQuestion?.id ? true : hovered)}>
                        {<View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <MyAppText style={styles.cell}>{item.id} - {item.question}</MyAppText>
                            <TouchableOpacity onPress={() => {
                                setToDeleteQuestion(item.id);
                                setIsDeleteModalVisible(true);
                            }}>
                                <FontAwesomeIcon icon={faTrashCan as IconDefinition} fontSize={16} color={theme.colors.iconColor} />
                            </TouchableOpacity>
                        </View>}
                    </View>
                )}
            </Hoverable>
        </TouchableOpacity>;
    }

    if (isLoading) {
        return <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
            <ActivityIndicator size='large' color={theme.colors.iconColor} />
        </View>;
    }
    return (
        <View style={styles.container}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <MyAppText style={styles.headerText}>{translate('frequentlyAskedQuestions')}</MyAppText>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                    <CustomButton onPress={() => {
                        const lastId = frequentlyAskedQuestions[frequentlyAskedQuestions.length - 1]?.id ?? 0;
                        setFrequentlyAskedQuestions([
                            ...frequentlyAskedQuestions,
                            {
                                id: lastId + 1,
                                question: '',
                                answer: '',
                                isPublic: false,
                            }
                        ]);
                        setSelectedFrequentlyAskedQuestion({
                            id: lastId + 1,
                            question: '',
                            answer: '',
                            isPublic: false,
                        });
                    }} type='secondary' text={translate('newQuestion')} />
                    <CustomButton onPress={() => saveFrequentlyAskedQuestions()} text={translate('save')} />
                </View>
            </View>
            <View style={{
                flex: 1,
                flexDirection: 'row',
                gap: 20
            }}>
                <View style={styles.leftArea}>
                    <ScrollView contentContainerStyle={{ flex: 1 }}>
                        <FlatList
                            data={frequentlyAskedQuestions}
                            renderItem={renderItem}
                            contentContainerStyle={{ gap: 10 }}
                        />
                    </ScrollView>
                </View>
                <View style={styles.rightArea}>
                    {
                        selectedFrequentlyAskedQuestion &&
                        <View style={styles.questionaryContainer((windowInfo.isMobileOrTablet))}>
                            <FormInput
                                label={translate('question')}
                                placeholder=''
                                value={selectedFrequentlyAskedQuestion.question}
                                disabled={false}
                                onChangeText={(value) => {
                                    const updated: FrequentlyAskedQuestion = {
                                        ...selectedFrequentlyAskedQuestion,
                                        question: value,
                                    };

                                    setSelectedFrequentlyAskedQuestion(updated);
                                    setFrequentlyAskedQuestions((prev) =>
                                        prev.map((q) =>
                                            q.id === updated.id ? updated : q
                                        )
                                    );
                                }}
                                invalid={() => false}
                                labelFontSize={14}
                                required={true}
                            />
                            <ExpandableCommentInput
                                value={selectedFrequentlyAskedQuestion.answer}
                                onChangeText={(value) => {
                                    const updated: FrequentlyAskedQuestion = {
                                        ...selectedFrequentlyAskedQuestion,
                                        answer: value,
                                    };

                                    setSelectedFrequentlyAskedQuestion(updated);
                                    setFrequentlyAskedQuestions((prev) =>
                                        prev.map((q) =>
                                            q.id === updated.id ? updated : q
                                        )
                                    );
                                }}
                                files={selectedFrequentlyAskedQuestion.file ? [selectedFrequentlyAskedQuestion.file] : []}
                                onFilesChange={(newFiles) => {
                                    const updated: FrequentlyAskedQuestion = {
                                        ...selectedFrequentlyAskedQuestion,
                                        file: newFiles[0],
                                    };

                                    setSelectedFrequentlyAskedQuestion(updated);
                                    setFrequentlyAskedQuestions((prev) =>
                                        prev.map((q) =>
                                            q.id === updated.id ? updated : q
                                        )
                                    );
                                }}
                                maxFiles={1}
                                label={translate('answer')}
                                FilePreviewComponent={FilePreview}
                                textAreaMaxHeight={350}
                                acceptedFileTypes={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']}
                                maxFileSize={2}
                                downloadableFiles
                            />
                            <MyCheckbox
                                label={translate('displayPublicly')}
                                textContainerStyle={{ fontSize: 14 }}
                                checked={selectedFrequentlyAskedQuestion.isPublic || false}
                                setChecked={(value) => {
                                    const updated: FrequentlyAskedQuestion = {
                                        ...selectedFrequentlyAskedQuestion,
                                        isPublic: value,
                                    };

                                    setSelectedFrequentlyAskedQuestion(updated);
                                    setFrequentlyAskedQuestions((prev) =>
                                        prev.map((q) =>
                                            q.id === updated.id ? updated : q
                                        )
                                    );
                                }}

                            />
                        </View>
                    }
                </View>
            </View>

            {toDeleteQuestion &&
                <DeleteModal
                    onSubmit={() => {
                        setFrequentlyAskedQuestions(frequentlyAskedQuestions.filter(q => q.id !== toDeleteQuestion));
                        if (frequentlyAskedQuestions.length) {
                            setSelectedFrequentlyAskedQuestion(frequentlyAskedQuestions[0]);
                        } else {
                            setSelectedFrequentlyAskedQuestion(null);
                        }
                    }}
                    isModalVisible={isDeleteModalVisible}
                    itemName={`${translate('question')} - ${toDeleteQuestion}`}
                    setModalVisible={() => setIsDeleteModalVisible(false)}
                />}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        gap: 20
    },
    leftArea: {
        flex: 1,
    },
    rightArea: {
        flexGrow: 3,
        flex: 1,
    },
    questionaryContainer: (isMobile: boolean) => {
        return {
            flexDirection: isMobile ? 'row' : undefined,
            gap: 20,
        };
    },
    row: (hovered: boolean) => {
        return {
            borderWidth: 1,
            borderRadius: 8,
            borderColor: theme.colors.container.border,
            flex: 1,
            padding: 10,
            backgroundColor: hovered ? theme.colors.secondaryButton.hoverBackground : theme.colors.secondaryButton.background,
        };
    },
    cell: {
        fontSize: 14,
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        maxWidth: '100%'
    },
    headerText: {
        fontSize: 16,
        color: theme.colors.neutral['500'],
        fontWeight: '600',
    },
}));
